import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { AlertService } from '../../../shared/alert/alert.service';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Principal } from '../../../shared';
import { VIEW_NAMES_PERMISSION } from '../../../shared/auth/permissions';
import { LotService } from '../../../shared/inventory/service/lot.service';
import { LotAttributeColumnModel, LotColumnCategory, LotColumnModel } from '../../../shared/inventory/model/lot.model';

@Component({
    selector: 'jhi-lot-columns',
    templateUrl: './lot-columns.component.html',
    styleUrls: [
        './lot-columns.component.scss'
    ]
})
export class LotColumnsComponent implements OnInit {

    @Output() columnsSelectedEvent = new EventEmitter<LotColumnModel[]>();

    @ViewChild('columnsDropdown') columnsDropdown: NgbDropdown;

    staticColumns: LotColumnModel[] = [];
    lotSourceColumns: LotColumnModel[] = [];
    lotAttributeColumns: LotColumnModel[] = [];

    constructor(private lotService: LotService,
                private alertService: AlertService,
                private principal: Principal) {
    }

    ngOnInit(): void {
        this.load();
    }

    load() {
        this.createStaticColumns();
        this.createLotSourceColumns();
        this.lotAttributeColumns = [];
    }

    dropdownToggle() {
        this.lotService.getLotAttributeColumns().subscribe(
            (res: HttpResponse<LotAttributeColumnModel[]>) => this.onGetColumnsSuccess(res.body)
        );
    }

    apply() {
        this.columnsDropdown.close();
        this.columnsSelectedEvent.emit(this.concatAllColumns());
    }

    onSearchColumn(evt: any) {
        const searchString = evt.target.value.toLowerCase();

        if (searchString) {
            this.filterColumns(this.concatAllColumns(), searchString);
        } else {
            this.concatAllColumns().forEach((column: LotColumnModel) => column.visible = true);
        }
    }

    checkAreColumnsVisible(columns: LotColumnModel[]): boolean {
        return columns.filter((column: LotColumnModel) => column.visible).length > 0;
    }

    private filterColumns(columns: LotColumnModel[], searchString: string) {
        columns.forEach((column: LotColumnModel) => {
            if (!column.displayName.toLowerCase().includes(searchString)) {
                column.visible = false;
            }
        });
    }

    private onGetColumnsSuccess(columns: LotAttributeColumnModel[]) {
        const lotAttributeColumnsSelectedMap = {};
        this.lotAttributeColumns.forEach((column) => lotAttributeColumnsSelectedMap[column.id] = column.selected);
        this.lotAttributeColumns = columns.map((column: LotAttributeColumnModel) =>
            new LotColumnModel(column.id.toString(),
                column.alias ? `${column.alias} (${column.name}) ` : column.name,
                LotColumnCategory.ATTRIBUTE,
                lotAttributeColumnsSelectedMap[column.id],
                true));
    }

    private concatAllColumns(): LotColumnModel[] {
        return [].concat(this.staticColumns, this.lotSourceColumns, this.lotAttributeColumns);
    }

    private createStaticColumn(id: string, name: string, selected: boolean) {
        return new LotColumnModel(id, name, LotColumnCategory.STATIC, selected, true);
    }

    private createStaticColumns() {
        this.staticColumns.push(this.createStaticColumn('lotId', 'inventoryManagerApp.lot.lotId', false));
        this.staticColumns.push(this.createStaticColumn('lotUUID', 'inventoryManagerApp.lot.lotUUID', false));
        this.staticColumns.push(this.createStaticColumn('stockId', 'inventoryManagerApp.lot.stockId', true));
        this.staticColumns.push(this.createStaticColumn('gid', 'inventoryManagerApp.lot.gid', true));
        this.staticColumns.push(this.createStaticColumn('mgid', 'inventoryManagerApp.lot.mgid', true));
        if (this.principal.hasAnyAuthorityDirect(VIEW_NAMES_PERMISSION)) {
            this.staticColumns.push(this.createStaticColumn('designation', 'inventoryManagerApp.lot.designation', true));
        }
        this.staticColumns.push(this.createStaticColumn('status', 'inventoryManagerApp.lot.status', true));
        this.staticColumns.push(this.createStaticColumn('locationName', 'inventoryManagerApp.lot.locationName', true));
        this.staticColumns.push(this.createStaticColumn('unitName', 'inventoryManagerApp.lot.unitName', true));
        this.staticColumns.push(this.createStaticColumn('actualBalance', 'inventoryManagerApp.lot.actualBalance', true));
        this.staticColumns.push(this.createStaticColumn('availableBalance', 'inventoryManagerApp.lot.availableBalance', true));
        this.staticColumns.push(this.createStaticColumn('reservedTotal', 'inventoryManagerApp.lot.reservedTotal', true));
        this.staticColumns.push(this.createStaticColumn('withdrawalTotal', 'inventoryManagerApp.lot.withdrawalTotal', true));
        this.staticColumns.push(this.createStaticColumn('pendingDepositsTotal', 'inventoryManagerApp.lot.pendingDeposits', true));
        this.staticColumns.push(this.createStaticColumn('notes', 'inventoryManagerApp.lot.notes', true));
        this.staticColumns.push(this.createStaticColumn('createdByUsername', 'inventoryManagerApp.lot.createdByUsername', true));
        this.staticColumns.push(this.createStaticColumn('createdDate', 'inventoryManagerApp.lot.createdDate', true));
        this.staticColumns.push(this.createStaticColumn('lastDepositDate', 'inventoryManagerApp.lot.lastDepositDate', true));
        this.staticColumns.push(this.createStaticColumn('lastWithdrawalDate', 'inventoryManagerApp.lot.lastWithdrawalDate', true));
    }

    private createLotSourceColumns() {
        this.lotSourceColumns.push(this.createStaticColumn('sourceUid', 'inventoryManagerApp.lot.sourceLotUid', false));
        this.lotSourceColumns.push(this.createStaticColumn('sourceStockId', 'inventoryManagerApp.lot.sourceStockId', false));
    }
}
